import { intl } from '@fullstory/intl';
import { noop } from 'lodash';
import { ComponentProps, useCallback } from 'react';
import { Select } from '../Select';
import { useSelectMenuFilter } from '../SelectMenuFilter';
import { SelectMenuOptionsProps, useSelectMenuOptions } from '../SelectMenuOptions';
import { useSelectMenuSelect } from '../SelectMenuSelect';
import { OptionType } from './types';
import { useSelectMenuHandle } from './useSelectMenuHandle';

type SelectProps = Omit<
  React.ComponentProps<typeof Select>,
  'open' | 'onOpen' | 'onClose' | 'displayName' | 'maxHeight' | 'multiple' | 'contentWidth'
>;
export interface SelectMenuProps<T>
  extends Omit<SelectMenuOptionsProps<T>, 'onClick'>,
    SelectProps {
  /**
   * Callback for when the selected option changes.
   */
  onChange: (option: OptionType<T> | null) => void;

  /**
   * Placeholder text to display on the SelectMenu button when the value is null
   *
   * Defaults to: "Select an option..."
   */
  placeholder?: string;

  /**
   * The placeholder to be used in the filter text input.
   *
   * Defaults to: "Search"
   */
  filterPlaceholder?: string;

  /**
   * Optional hook into the menu's close.
   */
  onClose?: () => void;

  /**
   * Optional configuration / callback to determine if the menu should close when an option is selected.
   */
  closeOnSelect?: boolean | ((option: OptionType<T> | null) => boolean);

  /**
   * Optional filter function
   *
   * Defaults to filtering by the OptionType::label i.e. option => option.label
   */
  filterFn?: (
    items: OptionType<T>[],
    filterText: string,
  ) => OptionType<T>[] | Promise<OptionType<T>[]>;

  /**
   * Optional flag to indicate if the select menu should initially start open.
   */
  initialOpen?: boolean;

  /**
   * Optional displayName prop
   */
  displayName?: ComponentProps<typeof Select>['displayName'];
}

export function useSelectMenu<T>(props: SelectMenuProps<T>) {
  const {
    allowUngrouped,
    allowUnsortedGroups,
    groupDividers,
    onChange,
    selected,
    options,
    clearable,
    placeholder = intl.formatMessage({ defaultMessage: 'Select an option...', id: 'o/42q7' }),
    filterPlaceholder = intl.formatMessage({ defaultMessage: 'Search', id: 'xmcVZ0' }),
    compact,
    alwaysHovered,
    tooltipProps,
    onClose = noop,
    onHighlight,
    filterFn,
    innerRef,
    displayName: displayNameProp,
    closeOnSelect = true,
    ...rest
  } = props;

  /**
   * Filter Props
   */
  const { filteredItems, filterProps, filterValue, Filter, filterRef } = useSelectMenuFilter(
    options,
    {
      compact,
      placeholder: filterPlaceholder,
    },
    filterFn,
  );
  const { onChange: onFilterChange } = filterProps;

  /**
   * Select Props
   */
  const displayName = selected ? displayNameProp ?? selected.label : placeholder;
  const rightAdornment = selected && selected.rightAdornment ? selected.rightAdornment : null;
  const handleClose = useCallback(() => {
    onClose();
    // reset filter on close
    onFilterChange('');
  }, [onClose, onFilterChange]);
  const { selectProps, Select } = useSelectMenuSelect({
    displayName,
    rightAdornment,
    onClose: handleClose,
    ...rest,
  });
  const { onClose: onSelectClose } = selectProps;

  /**
   * Options Props
   */
  const handleChange = useCallback(
    (option: OptionType<T> | null) => {
      const shouldCloseOnSelect =
        typeof closeOnSelect === 'boolean' ? closeOnSelect : closeOnSelect(option);
      if (shouldCloseOnSelect) {
        // Order is important here. We need to close the menu first so that the memoized
        // logic in the Menu component doesn't recompute unnecessarily.
        onSelectClose();
      }
      onChange(option);
    },
    [onChange, onSelectClose, closeOnSelect],
  );
  const { optionsProps, Options } = useSelectMenuOptions({
    allowUngrouped,
    allowUnsortedGroups,
    groupDividers,
    options: filteredItems,
    onClick: handleChange,
    onHighlight,
    compact,
    selected,
    clearable,
    alwaysHovered: alwaysHovered || filterValue.length > 0,
    tooltipProps,
  });

  /**
   * Implement imperative scroll to handlers
   */
  useSelectMenuHandle(selectProps.onOpen, optionsProps.innerRef, innerRef);

  return {
    Select,
    selectProps,
    Filter,
    filterProps,
    filterRef,
    Options,
    optionsProps,
  };
}
